import * as React from 'react';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Input from '@components/Input';
import Button, { BUTTON_TYPE } from '@components/Button';
import Spinner from '@components/Spinner';

type Props = {
  isSending: boolean;
  onSubmit: ({ password }: { password: string }) => void;
};

export default function NewPasswordForm({ isSending, onSubmit }: Props) {
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: 'onSubmit' });

  const password = React.useRef({});
  password.current = watch('password', '');
  const { t } = useTranslation('newPassword');

  const [isDirty, setIsDirty] = React.useState<{
    confirmPassword: boolean;
    password: boolean;
  }>({
    confirmPassword: false,
    password: false,
  });

  const { ref: passwordRef, ...passwordInputProps } = register('password', {
    required: t('form.errorsState.invalid.password'),
  });

  const { ref: confirmPasswordRef, ...confirmPasswordInputProps } = register(
    'confirmPassword',
    {
      required: t('form.errorsState.invalid.password'),
      validate: (value) =>
        value === password.current ||
        t('form.errorsState.invalid.passwordMismatch'),
      minLength: {
        value: 6,
        message: t('form.errorsState.invalid.passwordMinLength'),
      },
    }
  );

  function handleOnSubmit({
    confirmPassword,
    password,
  }: {
    confirmPassword: string;
    password: string;
  }) {
    setIsDirty({
      confirmPassword: confirmPassword.length > 0,
      password: password.length > 0,
    });
    onSubmit({ password });
  }

  return (
    <div className={styles.container}>
      <form
        className={styles.newPassForm}
        onSubmit={handleSubmit(handleOnSubmit as any)}
        noValidate
      >
        <Input
          hasValue={isDirty?.password ?? false}
          label={t('form.labels.password')}
          type="password"
          placeholder={'••••••••'}
          inputRef={passwordRef}
          {...passwordInputProps}
          error={errors?.password?.message}
        />

        <Input
          hasValue={isDirty?.confirmPassword ?? false}
          label={t('form.labels.repeatPassword')}
          type="password"
          placeholder={'••••••••'}
          inputRef={confirmPasswordRef}
          {...confirmPasswordInputProps}
          error={errors?.confirmPassword?.message}
        />

        <Button type={BUTTON_TYPE.PRIMARY} fullWidth>
          <div className={!isSending ? styles.label : styles.hide}>
            {t('form.labels.setNewPassword')}
          </div>
          {isSending && (
            <div className={styles.spinnerContainer}>
              <Spinner isWhite isMid />
            </div>
          )}
        </Button>
      </form>
    </div>
  );
}
